/**
 * 公開予約ページ限定の軽量 i18n（ja/en）。
 * 管理画面は日本語のみのため、ライブラリを追加せず公開ページだけ翻訳オブジェクトで切り替える。
 * 計画 FE Phase 1（C11）/ feast-fe ADR 0040。
 */

export type PublicReservationLang = 'ja' | 'en';

const dict = {
  // 共通
  loading: { ja: '読み込み中...', en: 'Loading...' },
  fetchError: { ja: '情報の取得に失敗しました。時間をおいて再度お試しください。', en: 'Failed to load. Please try again later.' },
  next: { ja: '次へ', en: 'Next' },
  back: { ja: '戻る', en: 'Back' },
  people: { ja: '名', en: ' people' },
  yen: { ja: '円', en: ' JPY' },
  notAccepting: { ja: '現在予約を受け付けていません', en: 'This store is not currently accepting reservations.' },
  previewNote: { ja: 'プレビューのため送信はできません', en: 'This is a preview. Submission is disabled.' },
  previewBanner: {
    ja: 'プレビュー表示 — お客様の予約画面と同じ見た目です（送信はできません）',
    en: 'Preview — same as the customer reservation page (submission disabled)',
  },

  // ステップ
  stepDateTime: { ja: '日時', en: 'Date & Time' },
  stepInfo: { ja: 'お客様情報', en: 'Your Info' },
  stepConfirm: { ja: '確認', en: 'Confirm' },
  stepPayment: { ja: 'お支払い', en: 'Payment' },
  stepDone: { ja: '完了', en: 'Done' },

  // 日時選択
  selectDate: { ja: '日付を選択', en: 'Select a date' },
  selectTime: { ja: '時間を選択', en: 'Select a time' },
  selectPartySize: { ja: '人数を選択', en: 'Party size' },
  closedDay: { ja: '休業日', en: 'Closed' },
  noSlots: { ja: 'この日に空き枠はありません', en: 'No available slots on this date.' },
  remainingSuffix: { ja: '組', en: ' left' },
  acceptanceUntil: { ja: 'まで予約可能', en: ' is the last day open for booking' },
  full: { ja: '満席', en: 'Full' },
  // 枠不可理由（Wave A・4／フィードバック4 B2）。
  // チップ内は時刻＋2文字ラベルで見切れを防ぐ。フル文言は tooltip/aria に回す。
  // AFTER_HOURS は満席表現を使わず「締切」（営業時間超過で受付を締め切る意）。
  afterHours: { ja: '締切', en: 'Closed' },
  afterHoursNote: {
    ja: '指定できません（営業時間を超えます）',
    en: 'Not available (would exceed business hours).',
  },
  fullNote: {
    ja: '満席のため指定できません',
    en: 'Fully booked and unavailable.',
  },
  // 深夜営業（例 17:00〜翌3:00）の翌未明枠に付ける補助表記。ja「翌1:00」/ en「+1 day 1:00」
  nextDayPrefix: { ja: '翌', en: '+1 ' },

  // 情報入力
  guestName: { ja: 'お名前', en: 'Name' },
  email: { ja: 'メールアドレス', en: 'Email' },
  emailHelp: { ja: '予約内容の確認メールをお送りします', en: 'A confirmation email will be sent to this address.' },
  phone: { ja: '電話番号', en: 'Phone' },
  optional: { ja: '（任意）', en: ' (optional)' },
  course: { ja: 'コース', en: 'Course' },
  noCourse: { ja: 'コースなし（席のみ）', en: 'No course (seat only)' },
  // 税込表示（フィードバック4。金額はお客様向けに税込で表示する）
  taxIncluded: { ja: '（税込）', en: ' (tax incl.)' },
  // コース必須時のプレースホルダ・エラー（フィードバック4）
  selectCoursePlaceholder: { ja: 'コースを選択してください', en: 'Select a course' },
  courseRequiredError: { ja: 'コースを選択してください', en: 'Please select a course.' },
  // ご滞在目安（フィードバックB4。コースの滞在時間 or 店舗デフォルト。ja「ご滞在目安: 約120分」）
  stayEstimatePrefix: { ja: 'ご滞在目安: 約', en: 'Estimated stay: approx. ' },
  stayEstimateSuffix: { ja: '分', en: ' min' },
  // コース「選択なし」時の案内（Wave A・3a。store default stay を明記）
  noCourseStayPrefix: {
    ja: 'コースを選択しない場合、店舗のデフォルトのご滞在目安（約',
    en: 'Without a course, the store default stay (approx. ',
  },
  noCourseStaySuffix: {
    ja: '分）でのご予約になります。',
    en: ' min) will be applied.',
  },
  // コース選択見出し（Wave A・3b。分かりやすい選択 UI 用）
  courseSectionTitle: { ja: 'コースを選択', en: 'Choose a course' },
  courseSectionOptional: {
    ja: 'コースは任意です。選ばない場合は席のみのご予約になります。',
    en: 'A course is optional. Without one, this is a seat-only reservation.',
  },
  courseStayLabel: { ja: '滞在目安 約', en: 'Stay approx. ' },
  courseStaySuffix: { ja: '分', en: ' min' },
  // 承認要否メッセージ（Wave A・5。approvalMode で出し分け・トップ/確認の目立つ位置）
  approvalManualNotice: {
    ja: '店舗の承認後にご予約が確定します（この時点では確定ではありません）。',
    en: 'Your reservation is confirmed after the store approves it (not confirmed yet).',
  },
  approvalAutoNotice: {
    ja: 'このままご予約が確定します（本予約をもって確定）。',
    en: 'Your reservation will be confirmed as you submit it.',
  },
  note: { ja: 'その他ご要望', en: 'Requests' },
  // 同意文言（ADR 0058: 店舗と FEAST の個人情報の取り扱い＝共同利用へ1チェックで同意。
  //  ja「[店舗名]の[個人情報の取り扱い]、およびFEAST株式会社の[予約プライバシーポリシー]に同意する」）
  privacyAgreePrefix: { ja: '', en: 'I agree to ' },
  privacyPossessive: { ja: 'の', en: "'s " },
  privacyStoreFallback: { ja: '当店', en: 'the store' },
  privacyPolicy: { ja: '個人情報の取り扱い', en: 'privacy policy' },
  // 店舗と FEAST を並置する接続詞、および FEAST 側プラポリのリンク文言（ADR 0058）
  privacyAgreeConnector: { ja: '、および', en: ' and ' },
  privacyFeastName: { ja: 'FEAST株式会社', en: 'FEAST Corp' },
  privacyFeastPolicy: { ja: '予約プライバシーポリシー', en: 'Reservation Privacy Policy' },
  privacyAgreeSuffix: { ja: 'に同意する', en: '.' },
  // 匿名化・仮名化の注記（ADR 0058。個人情報入力/同意付近・確認画面に表示。リンクは予約プラポリ /legal/user-privacy）
  anonymizationNotice: {
    ja: 'ご入力いただいた氏名・電話番号・メールアドレスは、ご予約の来店日から15日後に復元不可能な形で削除・匿名化されます（統計目的の識別子のみ仮名化して保持）',
    en: 'Your name, phone number and email address will be deleted or irreversibly anonymized 15 days after your visit date (only a pseudonymized identifier is kept for statistics)',
  },
  anonymizationPolicyLink: {
    ja: '（詳しくは予約プライバシーポリシー）',
    en: ' (see the Reservation Privacy Policy)',
  },
  requiredError: { ja: 'は必須です', en: ' is required.' },
  invalidEmail: {
    ja: 'メールアドレスの形式が正しくありません（例: mail@example.com）',
    en: 'Invalid email address (e.g. mail@example.com).',
  },
  invalidPhone: {
    ja: '電話番号の形式が正しくありません（例: 090-1234-5678）',
    en: 'Invalid phone number (e.g. 090-1234-5678).',
  },
  agreeError: { ja: '個人情報の取り扱いへの同意が必要です', en: 'You must agree to the privacy policy.' },

  // 確認
  confirmTitle: { ja: '予約内容の確認', en: 'Confirm your reservation' },
  dateTime: { ja: '日時', en: 'Date & time' },
  partySize: { ja: '人数', en: 'Party size' },
  submit: { ja: 'この内容で予約する', en: 'Submit reservation' },
  submitting: { ja: '送信中...', en: 'Submitting...' },
  submitError: { ja: '予約の送信に失敗しました。', en: 'Failed to submit the reservation.' },

  // 完了
  doneTitleAuto: { ja: 'ご予約が確定しました', en: 'Your reservation is confirmed' },
  doneTitleManual: { ja: 'ご予約リクエストを受け付けました', en: 'Your reservation request has been received' },
  doneBodyAuto: {
    ja: '確認メールをお送りしました。当日のご来店をお待ちしております。',
    en: 'A confirmation email has been sent. We look forward to seeing you.',
  },
  doneBodyManual: {
    ja: '店舗が内容を確認後、確定のご連絡をお送りします。確定までしばらくお待ちください。',
    en: 'The store will review your request and send a confirmation. Please wait for the confirmation email.',
  },
  reservationNumber: { ja: '予約番号', en: 'Reservation number' },
  manageHint: {
    ja: '予約内容の確認・キャンセルは、お送りしたメールに記載のリンクから行えます。',
    en: 'To view or cancel your reservation, use the link in the email we sent you.',
  },

  // 来店予定日時の注記・店舗の連絡手段（フィードバック5・B7）
  visitDateTimeNote: {
    ja: '※ ご来店予定の日時です（この時間にご予約が確定しています）',
    en: '* This is your scheduled visit time (your reservation is set for this time).',
  },
  storeNameLabel: { ja: '店舗名', en: 'Restaurant' },
  storeContactLabel: { ja: '連絡先', en: 'Contact' },
  storeGuideTitle: { ja: '店舗・お問い合わせ', en: 'Restaurant & contact' },

  // 照会/キャンセル
  lookupTitle: { ja: 'ご予約の確認', en: 'Your reservation' },
  lookupNotFound: {
    ja: '予約が見つかりませんでした。リンクをご確認ください。',
    en: 'Reservation not found. Please check your link.',
  },
  status: { ja: 'ステータス', en: 'Status' },
  cancelButton: { ja: '予約をキャンセルする', en: 'Cancel reservation' },
  cancelConfirmTitle: { ja: '予約のキャンセル', en: 'Cancel reservation' },
  cancelConfirmBody: {
    ja: 'この予約をキャンセルします。よろしいですか？',
    en: 'Are you sure you want to cancel this reservation?',
  },
  cancelYes: { ja: 'キャンセルする', en: 'Yes, cancel' },
  cancelNo: { ja: '戻る', en: 'Back' },
  cancelled: { ja: '予約をキャンセルしました。', en: 'Your reservation has been cancelled.' },
  cancelError: { ja: 'キャンセルに失敗しました。', en: 'Failed to cancel.' },
  cancelDeadlinePassed: {
    ja: 'キャンセル期限を過ぎています。店舗へご連絡ください。',
    en: 'The cancellation deadline has passed. Please contact the store directly.',
  },

  // ステータス表示（照会ページ）
  statusRequested: { ja: '承認待ち', en: 'Requested' },
  statusConfirmed: { ja: '確定', en: 'Confirmed' },
  statusRejected: { ja: 'お断り', en: 'Declined' },
  statusCancelled: { ja: 'キャンセル済み', en: 'Cancelled' },
  statusCompleted: { ja: '来店済み', en: 'Completed' },
  statusNoShow: { ja: '不来店', en: 'No-show' },

  // 言語切替（言語名はどちらの言語でも同一表記）
  langJa: { ja: '日本語', en: '日本語' },
  langEn: { ja: 'EN', en: 'EN' },

  // カレンダー・日時整形
  weekdays: { ja: '日,月,火,水,木,金,土', en: 'Sun,Mon,Tue,Wed,Thu,Fri,Sat' },
  monthNames: {
    ja: '1月,2月,3月,4月,5月,6月,7月,8月,9月,10月,11月,12月',
    en: 'Jan,Feb,Mar,Apr,May,Jun,Jul,Aug,Sep,Oct,Nov,Dec',
  },
  prevMonth: { ja: '前の月', en: 'Previous month' },
  nextMonth: { ja: '次の月', en: 'Next month' },
  // 残枠表示: ja「残2組」/ en「2 left」
  remainingPrefix: { ja: '残', en: '' },

  // 確認・照会画面の回答表示
  yes: { ja: 'はい', en: 'Yes' },
  no: { ja: 'いいえ', en: 'No' },

  reservationHeading: { ja: 'ご予約', en: 'Reservation' },

  // 店舗情報セクション（フィードバック2・契約E。設定済みの項目のみ表示する）
  storeInfoTitle: { ja: '店舗情報', en: 'Restaurant Information' },
  addressLabel: { ja: '住所', en: 'Address' },
  accessLabel: { ja: 'アクセス', en: 'Access' },
  hoursLabel: { ja: '営業時間', en: 'Hours' },
  closedDaysLabel: { ja: '定休日', en: 'Closed days' },
  paymentLabel: { ja: 'お支払い', en: 'Payment' },
  serviceChargeLabel: { ja: 'サービス料・チャージ', en: 'Service charge' },
  cancellationLabel: { ja: 'キャンセル料', en: 'Cancellation policy' },
  dressCodeLabel: { ja: 'ドレスコード・服装', en: 'Dress code' },
  seatsLabel: { ja: '座席', en: 'Seating' },
  smokingLabel: { ja: '喫煙可否', en: 'Smoking' },
  childrenLabel: { ja: 'お子様の同伴', en: 'Children' },
  petLabel: { ja: 'ペットの同伴', en: 'Pets' },
  parkingLabel: { ja: '駐車場', en: 'Parking' },
  storeNotesLabel: { ja: '店舗注意事項', en: 'Notes' },
  photos: { ja: '写真', en: 'Photos' },

  // コース画像（フィードバック2・契約E）
  coursePhotos: { ja: 'コースの写真', en: 'Course photos' },
  viewAllPhotos: { ja: 'タップして全ての写真を見る', en: 'Tap to view all photos' },
  close: { ja: '閉じる', en: 'Close' },

  // 個人情報の取り扱い公開ページ（フィードバック3・TEXT 型の店舗の掲載本文）
  privacyPageHeadingSuffix: { ja: 'の個人情報の取り扱い', en: ' Privacy Policy' },
  privacyPageNotFound: {
    ja: 'この店舗の個人情報の取り扱いは公開されていません。',
    en: 'The privacy policy for this store is not available.',
  },

  // 公開ページ共通フッター（サービス提供者の明示・フィードバック3）
  footerServicePrefix: { ja: 'サービス提供: FEAST株式会社（', en: 'Service provided by FEAST Corp. (' },
  footerUserPrivacyLink: { ja: '予約プライバシーポリシー', en: 'Reservation Privacy Policy' },
  footerServiceSuffix: { ja: '）', en: ')' },

  // ---- 予約デポジット決済（Phase 2・3DS・統合契約 A7/B2） ----
  // 確認ステップのデポジット案内（決済必須店舗のみ）
  depositNoticeTitle: { ja: 'お支払いについて', en: 'About payment' },
  depositNoticeBody: {
    ja: 'ご予約の確定にはデポジット（前受金）のお支払いが必要です。次の画面でカード情報をご入力ください。',
    en: 'A deposit is required to confirm your reservation. You will enter your card details on the next screen.',
  },
  depositAmountLabel: { ja: 'デポジット金額', en: 'Deposit amount' },
  depositAmountPreviewNote: {
    ja: '※ 金額はお支払い画面で確定します',
    en: '* The exact amount is finalized on the payment screen.',
  },
  // キャンセルポリシー（返金段階）
  cancellationPolicyTitle: { ja: 'キャンセルポリシー', en: 'Cancellation policy' },
  cancellationTierRefund: {
    // 例: 「48時間前まで: 全額返金」/ 「until 48h before: 100% refund」
    ja: '時間前まで: ',
    en: 'h before: ',
  },
  cancellationTierPrefix: { ja: '開始', en: 'Until ' },
  refundPercentSuffix: { ja: '％返金', en: '% refund' },
  refundNoRefund: { ja: '返金なし', en: 'no refund' },
  refundFull: { ja: '全額返金', en: 'full refund' },
  // 返金額の概算換算（決済額が分かる場合に tier 行へ併記。ja「（約¥1,000）」/ en「 (approx. ¥1,000)」）
  refundApproxPrefix: { ja: '（約¥', en: ' (approx. ¥' },
  refundApproxSuffix: { ja: '）', en: ')' },
  // 手数料の扱い（refundFeePolicy）と FEAST の手数料方針
  refundFeeStoreAbsorbs: {
    ja: 'キャンセル時の決済手数料は店舗が負担し、上記の返金率どおりに返金されます。',
    en: 'The store covers any payment processing fees on cancellation; refunds follow the rates above.',
  },
  refundFeeCustomerBears: {
    ja: 'お客様都合のキャンセルでは、決済手数料分を差し引いて返金されます。',
    en: 'For customer-initiated cancellations, payment processing fees are deducted from the refund.',
  },
  feastNoFeeNote: {
    ja: 'FEASTは決済手数料を受け取りません。Stripe所定の決済手数料は別途発生します。',
    en: 'FEAST does not charge any payment fee. Stripe’s standard processing fees apply separately.',
  },
  // デポジット・キャンセルポリシーへの同意
  depositAgree: {
    ja: 'デポジットのお支払いとキャンセルポリシーに同意します',
    en: 'I agree to the deposit payment and the cancellation policy.',
  },
  depositAgreeError: {
    ja: 'デポジットとキャンセルポリシーへの同意が必要です',
    en: 'You must agree to the deposit and cancellation policy.',
  },
  // 決済ステップ
  paymentStepTitle: { ja: 'お支払い', en: 'Payment' },
  paymentStepGuide: {
    ja: 'カード情報をご入力のうえ、お支払いに進んでください。3Dセキュア認証が求められる場合があります。',
    en: 'Enter your card details and proceed. 3-D Secure authentication may be required.',
  },
  payButton: { ja: '支払う', en: 'Pay' },
  paying: { ja: '処理中...', en: 'Processing...' },
  paymentLoadError: {
    ja: '決済フォームの読み込みに失敗しました。時間をおいて再度お試しください。',
    en: 'Failed to load the payment form. Please try again later.',
  },
  paymentFailedRetry: {
    ja: 'お支払いに失敗しました。カード情報をご確認のうえ、再度お試しください。',
    en: 'Payment failed. Please check your card details and try again.',
  },
  paymentExpired: {
    ja: 'お支払いの受付時間を過ぎました。お手数ですが最初からやり直してください。',
    en: 'The payment window has expired. Please start over.',
  },
  paymentExpiryNote: {
    ja: 'このお支払いは時間制限があります。お早めにお手続きください。',
    en: 'This payment is time-limited. Please complete it promptly.',
  },
  startOver: { ja: '最初からやり直す', en: 'Start over' },
  // finalize（確定）
  finalizing: { ja: 'お支払いを確認しています...', en: 'Confirming your payment...' },
  finalizePending: {
    ja: 'お支払いの確認に時間がかかっています。数秒後に自動で再確認します。',
    en: 'Confirming your payment is taking a moment. We will retry automatically.',
  },
  finalizeRetry: { ja: '再確認する', en: 'Retry' },
  finalizeFailed: {
    ja: 'お支払いの確認に失敗しました。お手数ですが照会リンクから状況をご確認ください。',
    en: 'We could not confirm your payment. Please check the status from your lookup link.',
  },
  // 完了画面のデポジット表示
  depositPaidLabel: { ja: 'お支払い済みデポジット', en: 'Deposit paid' },
  manageLinkButton: { ja: '予約内容を確認する', en: 'View my reservation' },

  // 支払い表示（照会ページ）
  paymentAmount: { ja: 'お支払い金額', en: 'Payment amount' },
  refundedAmount: { ja: '返金額', en: 'Refunded amount' },
  paymentAuthorized: { ja: '与信済み', en: 'Authorized' },
  paymentCaptured: { ja: '決済済み', en: 'Paid' },
  paymentCanceled: { ja: '決済取消', en: 'Canceled' },
  paymentPartiallyRefunded: { ja: '一部返金済み', en: 'Partially refunded' },
  paymentRefunded: { ja: '返金済み', en: 'Refunded' },
  paymentFailed: { ja: '決済失敗', en: 'Payment failed' },
} as const;

export type PublicReservationDictKey = keyof typeof dict;

/** 言語に応じた翻訳関数 t(key) を返す */
export const makeT =
  (lang: PublicReservationLang) =>
  (key: PublicReservationDictKey): string =>
    dict[key][lang];

/** 公開ステータス→表示ラベルのキー */
export const publicStatusKey = (status: string): PublicReservationDictKey => {
  switch (status) {
    case 'REQUESTED':
      return 'statusRequested';
    case 'CONFIRMED':
      return 'statusConfirmed';
    case 'REJECTED':
      return 'statusRejected';
    case 'CANCELLED':
      return 'statusCancelled';
    case 'COMPLETED':
      return 'statusCompleted';
    case 'NO_SHOW':
      return 'statusNoShow';
    default:
      return 'statusRequested';
  }
};
